import { type ReactElement, useState, useMemo } from 'react';
import classnames from 'classnames/bind';
import dayjs from 'dayjs';
import { Typography, SearchField } from '@weareopenr/component-library';
import { type DesignAsset } from 'src/api/designPlatform/getTemplates';
import styles from '../DesignPlatformExportFlow.module.scss';

const cx = classnames.bind(styles);

interface SelectTemplateStepProps {
  templates: DesignAsset[];
  templatesLoading: boolean;
  selectedTemplateId: string | null;
  onSelectTemplate: (templateId: string) => void;
}

const formatDate = (isoString: string): string => dayjs(isoString).format('DD/MM/YY, HH:mm');

const formatSectionContents = (itemsPerSection: number[]): string => {
  if (!itemsPerSection || itemsPerSection.length === 0) {
    return 'No sections';
  }
  return itemsPerSection
    .map((items, index) => `Section ${String.fromCodePoint(65 + index)} - [${items} items]`)
    .join(', ');
};

const TemplateSkeleton = (): ReactElement => (
  <div className={styles['template-card-skeleton']}>
    <div className={styles['skeleton-radio']} />
    <div className={styles['skeleton-content']}>
      <div className={styles['skeleton-title']} />
      <div className={styles['skeleton-subtitle']} />
      <div className={styles['skeleton-text']} />
      <div className={styles['skeleton-text-short']} />
    </div>
    <div className={styles['skeleton-thumbnail']} />
  </div>
);

export const SelectTemplateStep = ({
  templates,
  templatesLoading,
  selectedTemplateId,
  onSelectTemplate,
}: SelectTemplateStepProps): ReactElement => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredTemplates = useMemo(() => {
    if (!searchQuery.trim()) {
      return templates;
    }
    const query = searchQuery.toLowerCase();
    return templates.filter((template) => template.title.toLowerCase().includes(query));
  }, [templates, searchQuery]);

  if (templatesLoading) {
    return (
      <div className={styles['step-content']}>
        <TemplateSkeleton />
        <TemplateSkeleton />
        <TemplateSkeleton />
      </div>
    );
  }

  if (!templates || templates.length === 0) {
    return (
      <div className={styles['step-content']}>
        <div className={styles['empty-state']}>
          <Typography size="xlarge" weight="semi-bold" className={styles['empty-state-title']}>
            No templates found.
          </Typography>
          <Typography size="medium" className={styles['empty-state-subtitle']}>
            Please create your first template before continuing
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={styles['step-content']}>
      <div className={styles['search-container']}>
        <SearchField
          isDisabled={false}
          placeholder="Search for a template"
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className={styles['template-list']}>
        {filteredTemplates.map((template) => {
          const isSelected = selectedTemplateId === template.id;
          return (
            <button
              key={template.id}
              type="button"
              className={cx('template-card', { 'template-card-selected': isSelected })}
              onClick={() => onSelectTemplate(template.id)}
            >
              <div className={styles['template-radio']}>
                <div className={cx('radio-outer', { 'radio-outer-selected': isSelected })}>
                  {isSelected && <div className={styles['radio-inner']} />}
                </div>
              </div>
              <div className={styles['template-info']}>
                <Typography size="xlarge" weight="bold">
                  {template.title}
                </Typography>
                <Typography size="xsmall" className={styles['template-meta']}>
                  ID: {template.id} | Last Edited: {formatDate(template.updated_at)}
                </Typography>
                <Typography size="xsmall" weight="bold" className={styles['template-contents-label']}>
                  Menu contents:
                </Typography>
                <Typography size="xsmall" className={styles['template-contents']}>
                  {formatSectionContents(template.items_per_section)}
                </Typography>
              </div>
              <div className={styles['template-preview']}>
                {template.thumbnail_url && (
                  <div className={styles['template-thumbnail']}>
                    <img src={template.thumbnail_url} alt={template.title} />
                  </div>
                )}
                {template.orientation && (
                  <Typography size="xsmall" className={styles['template-orientation']}>
                    {template.orientation.charAt(0).toUpperCase() + template.orientation.slice(1)}
                  </Typography>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
